import React, { useState } from 'react';
import {
    Button,
    Box,
    Typography,
    Stack,
    FormControl,
    TextField,
    IconButton,
    DialogContent,
    DialogActions,
    Alert,
} from '@mui/material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { Close } from '@mui/icons-material';
import { APP_CONFIG } from '../../../../config/app.config';
import { container } from '@infrastructure/container';
import { Connection } from '@domain/connection/models/Connection';


interface MontaCredentialsFormProps {
    onNewConnection: (connection: Connection) => void;
    onClose: () => void;
    /** When set, re-authorizes this existing connection instead of creating a new one. */
    reconnectConnectionId?: string;
}

const MontaCredentialsForm: React.FC<MontaCredentialsFormProps> = ({ onNewConnection, onClose, reconnectConnectionId }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    const [validationError, setValidationError] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);


    const connectWithMonta = async () => {
        setError(null);
        setValidationError(null);

        if (!username || !password) {
            setValidationError('Please fill in all fields');
            return;
        }

        setIsLoading(true);
        const request = reconnectConnectionId
            ? container.montaConnectorRepository.reconnect(reconnectConnectionId, username, password)
            : container.montaConnectorRepository.addMontaMarketplaceConnection(username, password);

        request.then((response) => {
            if (response.status === 'success') {
                onNewConnection(response.data);
            } else {
                setError(response.error?.message || 'Something went wrong');
            }
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setIsLoading(false);
        });
    };



    return (
        <>
            <DialogHeader>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="medium">
                        {reconnectConnectionId ? 'Reconnect Monta' : 'Connect Monta'}
                    </Typography>

                    <IconButton onClick={onClose} color="inherit">
                        <Close />
                    </IconButton>
                </Stack>
            </DialogHeader>

            <DialogContent>

                <Box>
                    <Typography variant="subtitle2" color="text.primary">
                        To connect to your Monta account, follow these steps:
                    </Typography>


                    <Typography variant="body2" color="text.secondary">
                        <ol style={{ paddingLeft: 20 }}>
                            <li>
                                <Typography variant="body2" color="text.secondary">
                                    Go to the <a href="https://www.montaportal.nl/Connect/PlatformDetails?platform=RestAPI" target="_blank" rel="noopener noreferrer">Monta</a> portal
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2" color="text.secondary">
                                    In the right top corner, click on +
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2" color="text.secondary">
                                    Enter JustStock as name and monta@juststock.ai as email and click Connect
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2" color="text.secondary">
                                    Fill the generated username and password in the form below
                                </Typography>
                            </li>


                        </ol>
                    </Typography>
                </Box>

                <FormControl fullWidth>
                    <TextField
                        label="Username"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        error={!!validationError}
                        helperText={validationError}
                    />
                    <TextField
                        label="Password"
                        type="password"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={!!validationError}
                        helperText={validationError}
                    />


                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}


                </FormControl>


            </DialogContent >

            <DialogActions sx={{ borderTop: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border, p: 3, justifyContent: 'flex-end' }}>

                <Button
                    variant="contained"
                    disabled={isLoading}
                    onClick={connectWithMonta}
                >
                    {reconnectConnectionId ? 'Reconnect' : 'Connect'}
                </Button>

            </DialogActions>


        </>
    );
};

export default MontaCredentialsForm;